import { dateFriendly } from '../helpers/date';
import { numberFormat } from '../helpers/utils';


/**
 * transform stock rule
 * @param {Object} item
 * @returns {Object}
 */
export const transformStockRule = (item = {}) => {
	return {
		id: item.id,
		name: item.name,
		priority: item.priority,
		totalBranches: item.total_branches,
		onlineRatio: item.online_ratio,
		minimum: item.minimum,
		createdAt: item.created_at ? dateFriendly(item.created_at, 'DD MMM YYYY') : null,
		updatedAt: item.updated_at ? dateFriendly(item.updated_at, 'DD MMM YYYY') : null,
		isActive: Boolean(item.is_active),
	};
};

/**
 * transform product
 * @param {Object} item product
 * @returns {Object}
 */
export const transformProduct = (item = {}) => {
	return {
		id: item.id,
		sku: item.sku,
	};
};

/**
 * transform products
 * @param {Array} items products
 * @returns {Array}
 */
export const transformProducts = (items = []) => {
	return items.map((item) => transformProduct(item));
};

/**
 * transform branch
 * @param {Object} item branch
 * @returns {Object}
 */
export const transformBranch = (item = {}) => {
	return {
		id: item.id,
		itecBranchId: item.itec_branch_id,
		nameEN: item.name_en,
		nameTH: item.name_th,
	};
};

/**
 * transform branches
 * @param {Array} items branches
 * @returns {Array}
 */
export const transformBranches = (items = []) => {
	return items.map((item) => transformBranch(item));
};

/**
 * transform stock rule detail
 * @param {Object} item
 * @returns {Object}
 */
export const transformStockRuleDetail = (item = {}) => {
	return {
		id: item.id,
		name: item.name,
		isActive: Boolean(item.is_active),
		onlineRatio: item.online_ratio,
		minimum: item.minimum,
		priority: item.priority,
		branches: transformBranches(item.branches) || [],
		isCategoryConfig: item.is_category_config,
		categoryIds: item.category_ids || [],
		products: transformProducts(item.products) || [],
		itecSkuExclusions: transformProducts(item.itec_sku_exclusions) || [],
		createdAt: item.created_at ? dateFriendly(item.created_at, 'DD MMM YYYY') : null,
		updatedAt: item.updated_at ? dateFriendly(item.updated_at, 'DD MMM YYYY') : null,
	};
};

/**
 * transform stock rules
 * @param {Array} items
 * @returns {Array}
 */
export const transformStockRules = (items = []) => {
	return items.map((item) => transformStockRule(item));
};

/**
 * transform channel rule
 * @param {Object} item channel rule
 * @returns {Object}
 */
export const transformChannelRole = (item = {}) => {
	return {
		id: item.id,
		name: item.name,
		priority: item.priority,
		totalProducts: numberFormat(item.total_products),
		totalConditions: item.total_conditions,
		createdAt: item.created_at ? dateFriendly(item.created_at, 'DD MMM YYYY') : null,
		updatedAt: item.updated_at ? dateFriendly(item.updated_at, 'DD MMM YYYY') : null,
		isActive: Boolean(item.is_active),
	};
};

/**
 * transform channel rules
 * @param {Array} item channel rules
 * @returns {Array}
 */
export const transformChannelRoles = (items = []) => {
	return items.map((item) => transformChannelRole(item));
};

/**
 * transform channel rule allocations
 * @param {Object} item
 * @returns {Object}
 */
export const transformChannelRuleAllocation = (item = {}) => {
	return {
		channelId: item.channel_id,
		channelName: item.channel_name,
		percentage: item.percentage,
	};
};

/**
 * transform channel rule allocations
 * @param {Array} items
 * @returns {Array}
 */
export const transformChannelRuleAllocations = (items = []) => {
	return items.map((item) => transformChannelRuleAllocation(item));
};

/**
 * transform channel rule allocations qty
 * @param {Object} item
 * @returns {Object}
 */
export const transformChannelRuleAllocationQty = (item = {}) => {
	return {
		channelId: item.channel_id,
		channelName: item.channel_name,
		qty: item.qty,
	};
};

/**
 * transform channel rule allocations qty
 * @param {Array} items
 * @returns {Array}
 */
export const transformChannelRuleAllocationQtys = (items = []) => {
	return items.map((item) => transformChannelRuleAllocationQty(item));
};

/**
 * transform channel rule conditions
 * @param {Object} item
 * @returns {Object}
 */
export const transformChannelRuleCondition = (item = {}) => {
	return {
		conditionQty: item.condition_qty,
		allocations: transformChannelRuleAllocationQtys(item.allocations),
	};
};

/**
 * transform channel rule conditions
 * @param {Array} items
 * @returns {Array}
 */
export const transformChannelRuleConditions = (items = []) => {
	return items.map((item) => transformChannelRuleCondition(item));
};

/**
 * transform channel rule detail
 * @param {Object} item
 * @returns {Object}
 */
export const transformChannelRuleDetail = (item = {}) => {
	return {
		id: item.id,
		name: item.name,
		priority: item.priority,
		branchGroupId: item.branch_group_id,
		categoryIds: item.category_ids || [],
		isCategoryConfig: item.is_category_config,
		products: transformProducts(item.products) || [],
		itecSkuExclusions: transformProducts(item.itec_sku_exclusions) || [],
		channelAllocations: transformChannelRuleAllocations(item.channel_allocations) || [],
		conditions: transformChannelRuleConditions(item.conditions) || [],
		createdAt: item.created_at ? dateFriendly(item.created_at, 'DD MMM YYYY') : null,
		updatedAt: item.updated_at ? dateFriendly(item.updated_at, 'DD MMM YYYY') : null,
		isActive: Boolean(item.is_active),
	};
};

/**
 * transform allocation channel rule
 * @param {Object} channel
 * @returns {Object} channel
 */
export const transformAllocationChannel = (data = {}) => {
	return {
		message: data.status,
		lastSyncedAt: data.last_synced_at ? dateFriendly(data.last_synced_at, 'DD MMM YYYY, HH:mm') : null,
	};
};
